import { useMemo } from "react";

import { NewsComment } from "./types";

/**
 * Hook to build a tree structure from a flat array of comments using a Map for O(1) lookups.
 * This approach is optimized for:
 * - Infinity scroll / pagination (just append to array)
 * - Adding new comments (just push to array)
 * - Getting children of a comment (O(1) lookup)
 *
 * @param comments - Flat array of comments with parent_id field
 * @returns Object with commentsMap, getChildren, and getTopLevelComments functions
 */
export const useCommentsTree = (comments: NewsComment[]) => {
  const commentsMap = useMemo(() => {
    const map = new Map<string | null, NewsComment[]>();

    comments.forEach((comment) => {
      const parentId = comment.parent_id ?? null;
      if (!map.has(parentId)) {
        map.set(parentId, []);
      }
      map.get(parentId)!.push(comment);
    });

    return map;
  }, [comments]);

  const getChildren = (parentId: string): NewsComment[] => {
    return commentsMap.get(parentId) || [];
  };

  const getTopLevelComments = (): NewsComment[] => {
    return commentsMap.get(null) || [];
  };

  return {
    commentsMap,
    getChildren,
    getTopLevelComments
  };
};
